'use client';
import React from 'react';
import {Dialog, DialogContent, DialogTitle} from './dialog';
import {Button} from './Button';
import type {BillingInterval} from '@features/subscription/subscriptionService';
import {pricing} from '@features/subscription/pricing';

interface UpgradeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
  teamSeatsCount?: number;
  isAnnual?: boolean;
  currentBillingInterval?: BillingInterval;
  targetTier?: 'plus' | 'pro';
  sourceTier?: 'plus' | 'pro';
  /** Override the "from" price label with the actual billed amount (e.g. "$10/mo") */
  currentPriceLabel?: string;
}

const teamPricing = {
  plus: {...pricing.team.plus, label: 'Team Plus'},
  pro: {...pricing.team.pro, label: 'Team Pro'},
};

const individualPricing = {
  plus: {...pricing.individual.plus, label: 'Plus'},
  pro: {...pricing.individual.pro, label: 'Pro'},
};

export function UpgradeConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
  teamSeatsCount = 1,
  isAnnual = false,
  currentBillingInterval,
  targetTier = 'plus',
  sourceTier = 'plus',
  currentPriceLabel,
}: UpgradeConfirmDialogProps) {
  const target = targetTier === 'pro' ? teamPricing.pro : teamPricing.plus;
  const source =
    sourceTier === 'pro' ? individualPricing.pro : individualPricing.plus;

  const teamPrice = isAnnual
    ? target.yearly * teamSeatsCount
    : target.monthly * teamSeatsCount;
  const teamPriceSuffix = isAnnual ? '/year' : '/mo';

  const currentPrice =
    currentPriceLabel ??
    (currentBillingInterval === 'year'
      ? `$${source.yearly}/year`
      : `$${source.monthly}/mo`);
  const currentPlanLabel =
    currentBillingInterval === 'year' ? `${source.label} Yearly` : source.label;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Change plan</DialogTitle>
        <div className='flex justify-between'>
          <span className='font-semibold'>Plan</span>
          {currentPlanLabel} → {target.label} {isAnnual ? 'Yearly' : 'Monthly'}
        </div>
        <div className='flex justify-between'>
          <span className='font-semibold'>Price</span>
          <span>
            {currentPrice} → ${teamPrice}
            {teamPriceSuffix}
          </span>
        </div>
        <div className='text-sm text-muted-foreground'>
          Only the difference will be charged. Your unused balance is deducted
          automatically.
        </div>
        <Button disabled={isLoading} onClick={onConfirm}>
          {isLoading ? 'Upgrading…' : 'Confirm upgrade'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
